import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { patch } from "common/helpers/HTTP";
import type { DetailedWorkspace } from "common/types";

async function addRentalAccountsRequest(
  workspaceId: string,
  rentalCount: number,
  planPriceId?: string,
): Promise<{ workspace: DetailedWorkspace }> {
  const { workspace } = await patch(`workspaces/${workspaceId}/rental`, {
    rental_count: rentalCount,
    item_price_id: planPriceId,
  });
  return { workspace };
}

export default function useRentalMutations() {
  const queryClient = useQueryClient();

  const { id: workspaceId, rental_subscription } =
    useSelectedWorkspaceContext();

  const { updateCachedWorkspaceDetails, updateCachedWorkspaceList } =
    useWorkspaceCacheUpdate();

  const {
    mutateAsync: addRentalAccounts,
    isPending: isAddingRentalAccounts,
    error: addRentalAccountsError,
  } = useMutation({
    mutationFn: ({
      rentalCount,
      planPriceId,
    }: {
      rentalCount: number;
      planPriceId?: string;
    }) => addRentalAccountsRequest(workspaceId, rentalCount, planPriceId),
    onSuccess: ({ workspace }) => {
      updateCachedWorkspaceDetails(workspaceId, () => workspace);
      updateCachedWorkspaceList(workspaceId, (draftWorkspace) => {
        const { status, rented_count } = workspace.rental_subscription;
        draftWorkspace.rental_subscription_status = status;
        draftWorkspace.rental_account_count = rented_count;
      });

      queryClient.invalidateQueries({
        queryKey: getQueryKeys(workspaceId).subscriptionKeys.renewalEstimate(
          rental_subscription.id,
        ),
      });
    },
  });

  return {
    addRentalAccounts,
    isAddingRentalAccounts,
    addRentalAccountsError,
  };
}
